"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSession } from "@/lib/auth-react";
import { authFetch } from "@/utils/auth-fetch";
import { locales, type Locale, LOCALE_COOKIE } from "@/i18n/config";

const LABEL_KEY: Record<Locale, "english" | "arabic" | "chinese"> = {
  en: "english",
  ar: "arabic",
  zh: "chinese",
};

/**
 * Locale picker. Persists the choice to `User.preferredLocale` (so it sticks
 * across devices and beats Accept-Language auto-detect at the next login) and
 * sets the `NEXT_LOCALE` cookie that drives server-side rendering, then
 * refreshes so the new locale + direction take effect.
 *
 * Works logged-out too (e.g. on /auth): with no access token it just writes
 * the cookie client-side.
 *
 * `tone` adapts colours for the dark dashboard header vs. light surfaces.
 */
export function LanguageSwitcher({
  tone = "light",
  className = "",
}: {
  tone?: "dark" | "light";
  className?: string;
}) {
  const active = useLocale() as Locale;
  const t = useTranslations("Common");
  const tMenu = useTranslations("UserMenu");
  const { data: session } = useSession();
  const [pending, setPending] = useState(false);

  async function choose(locale: Locale) {
    if (locale === active || pending) return;
    setPending(true);
    const token = session?.accessToken;
    if (token) {
      try {
        await authFetch("/api/users/me/locale", token, {
          method: "POST",
          body: JSON.stringify({ locale }),
        });
      } catch {
        // Fall through to the cookie write so the UI still switches.
      }
    }
    // Mirror the cookie client-side so the switch applies even when logged out
    // (and immediately, without waiting on the server round-trip).
    document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    // Hard reload: guarantees the server re-renders <html lang/dir> + messages
    // for the new locale (a soft refresh doesn't reliably update <html>).
    window.location.reload();
  }

  const isDark = tone === "dark";
  const base =
    "shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors disabled:opacity-50";
  const activeCls = isDark
    ? "bg-white text-[#27335D]"
    : "bg-[#27335D] text-white";
  const idleCls = isDark
    ? "text-white/70 hover:bg-white/10 hover:text-white"
    : "text-gray-600 hover:bg-gray-100";

  return (
    <div
      data-testid="languageSwitcher"
      className={`flex flex-col gap-1.5 ${className}`}
    >
      <span className={`text-xs ${isDark ? "text-white/50" : "text-gray-500"}`}>
        {tMenu("language")}
      </span>
      {/* Languages on their own row — every locale shown as a button (no
          dropdown), so 中文 & co. are visible at a glance. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {locales.map((locale) => (
          <button
            key={locale}
            type="button"
            dir={locale === "ar" ? "rtl" : "ltr"}
            disabled={pending}
            onClick={() => choose(locale)}
            data-testid={`languageOption-${locale}`}
            aria-pressed={locale === active}
            className={`${base} ${locale === active ? activeCls : idleCls}`}
          >
            {t(LABEL_KEY[locale])}
          </button>
        ))}
      </div>
    </div>
  );
}
